package org.example.zone360.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.window
import org.example.zone360.models.ClubAverages
import org.example.zone360.models.CoachTalkRatioData
import org.example.zone360.models.CompetitiveEnvironment
import org.example.zone360.models.CompetitiveEnvironmentSubScores
import org.example.zone360.models.MatchAnalysis
import org.example.zone360.models.MatchAnalysisSubScores
import org.example.zone360.models.MethodologyConsistency
import org.example.zone360.models.RecentTrend
import org.example.zone360.models.WidgetDetailResponse
import org.example.zone360.pages.widgets.CommonWidgetCard
import org.example.zone360.pages.widgets.competitiveWidget.CompetitiveEnvironmentWidget
import org.example.zone360.pages.widgets.developmentOrientation.DevelopmentOrientationWidget
import org.example.zone360.pages.widgets.helper.WidgetTooltipContent
import org.example.zone360.pages.widgets.matchAnalysisWidget.MatchAnalysisNewWidget
import org.example.zone360.pages.widgets.methodologyWidget.MethodologyConsistencyWidget
import org.example.zone360.pages.widgets.spiderChartModal.SpiderChartWidgetCard
import org.example.zone360.pages.widgets.topicDiversty.TopicDiversityEntry
import org.example.zone360.pages.widgets.topicDiversty.TopicDiversityWidget
import org.example.zone360.theme.Palette
import org.example.zone360.utils.Res
import org.jetbrains.compose.web.css.CSSSizeValue
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

@Composable
fun rememberWidgetRenderers(
    widgetData: ClubAverages?,
    recentTrend: RecentTrend? = null,
    showSelection: Boolean,
    sessionBorder: Boolean = false,
    selectedIds: Set<Int>,
    isCoachPage: Boolean = false,
    forceFullWidth: Boolean = false,
    onSelectionChange: (Int, Boolean) -> Unit,
): Map<Int, @Composable (Int) -> Unit> {

    val breakpoint = rememberBreakpoint()
    var widgetDetailResponse by remember { mutableStateOf<WidgetDetailResponse?>(null) }
    val cardWidth = when {
        forceFullWidth -> 100.percent
        sessionBorder -> 100.percent
        showSelection && window.screen.width > 1600 -> 18.percent
        window.screen.width > 1600 -> 24.4.percent
        breakpoint > Breakpoint.LG -> 32.percent
        breakpoint >= Breakpoint.MD -> 49.percent
        else -> 100.percent
    }

    return mapOf(
        1 to @Composable { displayNumber: Int ->
            CommonWidgetCard(
                modifier = Modifier.width(cardWidth),
                showSelection = showSelection,
                isSelected = selectedIds.contains(1),
                onSelectionChange = {
                    onSelectionChange(1, it)
                },
                data = CoachTalkRatioData(
                    value = widgetData?.coachTalkRatio?.score?.toInt() ?: 0,
                    clubAverage = widgetData?.coachTalkRatio?.score?.toInt() ?: 0,
                    target = "35-45%",
                    aligned = (widgetData?.coachTalkRatio?.score?.toInt() ?: 0) in 35..45
                ),
                widgetNo = displayNumber,
                widgetKey = "coach_talk_ratio",
                trendData = recentTrend?.coachTalkRatio.orEmpty(),
                widgetName = "Coach Talk Ratio",
                detailResponse = widgetDetailResponse,
                widgetGaugeID = Res.Id.coachTalkGauge,
                widgetColor = Palette.CoachTalkPrimaryBlue.rgbLight,
                isTalk = true,
                sessionBorder = sessionBorder,
                forceFullWidth = forceFullWidth,
                isCoachPage = isCoachPage,
                tooltipTitle = WidgetTooltipContent.coachTalkRatio.title,
                tooltipText = WidgetTooltipContent.coachTalkRatio.text
            )
        },
        2 to @Composable { displayNumber: Int ->
            CommonWidgetCard(
                modifier = Modifier.width(cardWidth),
                showSelection = showSelection,
                isSelected = selectedIds.contains(2),
                onSelectionChange = {
                    onSelectionChange(2, it)
                },
                data = CoachTalkRatioData(
                    value = widgetData?.sessionIntensity?.score?.toInt() ?: 0,
                    clubAverage = widgetData?.sessionIntensity?.score?.toInt() ?: 0,
                    target = "70-80",
                    aligned = (widgetData?.sessionIntensity?.score?.toInt() ?: 0) in 70..80
                ),
                widgetNo = displayNumber,
                trendData = recentTrend?.sessionIntensity.orEmpty(),
                widgetName = "Session Intensity Score",
                widgetKey = "session_intensity",
                widgetGaugeID = Res.Id.sessionIntensityGauge,
                widgetColor = Palette.SessionIntensityGreen.rgbLight,
                isTalk = false,
                sessionBorder = sessionBorder,
                forceFullWidth = forceFullWidth,
                isCoachPage = isCoachPage,
                tooltipTitle = WidgetTooltipContent.sessionIntensity.title,
                tooltipText = WidgetTooltipContent.sessionIntensity.text

            )
        },
        3 to @Composable { displayNumber: Int ->
            SpiderChartWidgetCard(
                widgetNo = displayNumber,
                widgetName = "Game Realism Score",
                sessionBorderWidth = 240,
                widgetColor = Palette.GameRealismPurple.rgbLight,
                showSelection = showSelection,
                trendData = recentTrend?.gameRealism.orEmpty(),
                isSelected = selectedIds.contains(3),
                onSelectionChange = {
                    onSelectionChange(3, it)
                },
                labels = listOf(
                    "Opposition",
                    "Decisions",
                    "Match Pressure",
                    "Directional Play",
                    "Transitions"
                ),
                values = listOf(
                    widgetData?.gameRealism?.subScores?.opposition?.toInt() ?: 0,
                    widgetData?.gameRealism?.subScores?.directionalPlay?.toInt() ?: 0,
                    widgetData?.gameRealism?.subScores?.matchPressure?.toInt() ?: 0,
                    widgetData?.gameRealism?.subScores?.decisionMaking?.toInt() ?: 0,
                    widgetData?.gameRealism?.subScores?.transitions?.toInt() ?: 0
                ),
                chartColor = "#9333EA",
                score = widgetData?.gameRealism?.score?.toInt() ?: 0,
                clubAvg = widgetData?.gameRealism?.score?.toInt() ?: 0,
                isCoachPage = isCoachPage,
                sessionBorder = sessionBorder,
                forceFullWidth = forceFullWidth,
                tooltipTitle = WidgetTooltipContent.gameRealism.title,
                tooltipText = WidgetTooltipContent.gameRealism.text,
                modifier = Modifier.width(cardWidth)
            )
        },
        4 to @Composable { displayNumber: Int ->
            val developmentScore =
                widgetData?.developmentOrientation?.score?.toInt() ?: 0

            val developmentStatus = when {
                developmentScore >= 70 -> "High"
                developmentScore >= 40 -> "Accurate"
                else -> "Low"
            }
            DevelopmentOrientationWidget(
                widgetNo = displayNumber,
                score = developmentScore,
                clubAvg = developmentScore,
                status = developmentStatus,
                trendData = recentTrend?.developmentOrientation.orEmpty(),
                modifier = Modifier.width(cardWidth),
                showSelection = showSelection,
                isSelected = selectedIds.contains(4),
                isCoachPage = isCoachPage,
                sessionBorder = sessionBorder,
                forceFullWidth = forceFullWidth,
                widgetColor = Palette.TacticalComplexityCyan.rgbLight,
                onSelectionChange = {
                    onSelectionChange(4, it)
                },
                tooltipTitle = WidgetTooltipContent.developmentOrientation.title,
                tooltipText = WidgetTooltipContent.developmentOrientation.text
            )
        },
        5 to @Composable { displayNumber: Int ->
            val tacticalScore = widgetData?.tacticalComplexity?.score?.toInt() ?: 0
            CommonWidgetCard(
                modifier = Modifier.width(cardWidth),
                showSelection = showSelection,
                isSelected = selectedIds.contains(5),
                onSelectionChange = {
                    onSelectionChange(5, it)
                },
                data = CoachTalkRatioData(
                    value = tacticalScore,
                    clubAverage = tacticalScore,
                    target = "45-65",
                    aligned = tacticalScore in 45..65
                ),
                widgetNo = displayNumber,
                trendData = recentTrend?.tacticalComplexity.orEmpty(),
                widgetName = "Tactical Complexity",
                widgetKey = "tactical_complexity",
                widgetGaugeID = Res.Id.tacticalComplexityGauge,
                widgetColor = Palette.TacticalComplexityCyan.rgbLight,
                isTalk = false,
                sessionBorder = sessionBorder,
                forceFullWidth = forceFullWidth,
                isCoachPage = isCoachPage,
                tooltipTitle = WidgetTooltipContent.tacticalComplexity.title,
                tooltipText = WidgetTooltipContent.tacticalComplexity.text
            )
        },
        6 to @Composable { displayNumber: Int ->
            val topicDiversityScore = widgetData?.topicDiversity?.score?.toInt() ?: 0
            val topicDiversityClubAvg = widgetData?.topicDiversity?.score?.toInt() ?: 0

            val topicEntryColors = listOf(
                "#4F8EF7", "#FB6F2A", "#22C55E", "#F59E0B", "#8B5CF6", "#06B6D4"
            )
            val hasTopicDiversityData = topicDiversityScore > 0 && !widgetData?.topicDiversity?.topicsCovered.isNullOrEmpty()

            val topicDiversityEntries =
                widgetData?.topicDiversity?.topicsCovered
                    ?.takeIf { it.isNotEmpty() }
                    ?.mapIndexed { i, p ->
                        TopicDiversityEntry(
                            label = p.topic,
                            percentage = p.percentage.toInt(),
                            colorHex = topicEntryColors[i % topicEntryColors.size]
                        )
                    }
                    ?: listOf(
                        TopicDiversityEntry(
                            label = "No Data",
                            percentage = 0,
                            colorHex = "#EC4899"
                        )
                    )

            TopicDiversityWidget(
                modifier = Modifier.width(cardWidth),
                widgetNo = displayNumber,
                widgetName = "Topic Diversity Score",
                widgetColor = Palette.TopicDiversityPink.rgbLight,
                score = topicDiversityScore,
                clubAverage = topicDiversityClubAvg,
                entries = topicDiversityEntries,
                trendData = recentTrend?.topicDiversity.orEmpty(),
                showSelection = showSelection,
                hasData = hasTopicDiversityData,
                isCoachPage = isCoachPage,
                sessionBorder = sessionBorder,
                forceFullWidth = forceFullWidth,
                isSelected = selectedIds.contains(displayNumber),
                onSelectionChange = { onSelectionChange(displayNumber, it) },
                tooltipTitle = WidgetTooltipContent.topicDiversity.title,
                tooltipText = WidgetTooltipContent.topicDiversity.text
            )
        },
        7 to @Composable { displayNumber: Int ->
            MethodologyConsistencyWidget(
                data = widgetData?.methodologyConsistency
                    ?: MethodologyConsistency(
                        consistentPrinciples = emptyList(),
                        score = 0
                    ),
                widgetNo = displayNumber,
                widgetName = "Methodology Consistency Score",
                widgetColor = Palette.MethodologyBlue.rgbLight,
                modifier = Modifier.width(cardWidth),
                showSelection = showSelection,
                trendData = recentTrend?.methodologyConsistency.orEmpty(),
                isSelected = selectedIds.contains(7),
                isCoachPage = isCoachPage,
                isTalk = false,
                sessionBorder = sessionBorder,
                forceFullWidth = forceFullWidth,
                widgetGaugeID = Res.Id.methodologyConsistencyGauge,
                onSelectionChange = {
                    onSelectionChange(7, it)
                },
                tooltipTitle = WidgetTooltipContent.methodologyConsistency.title,
                tooltipText = WidgetTooltipContent.methodologyConsistency.text,
                tooltipInline = true
            )
        },
        8 to @Composable { displayNumber: Int ->
            SpiderChartWidgetCard(
                widgetNo = displayNumber,
                widgetName = "Cognitive Training Score",
                widgetColor = Palette.CoachTalkPrimaryBlue.rgbLight,
                showSelection = showSelection,
                sessionBorderWidth = 300,
                isSelected = selectedIds.contains(8),
                onSelectionChange = {
                    onSelectionChange(8, it)
                },
                labels = listOf(
                    "Tactical Awareness",
                    "Scanning",
                    "Decision Making",
                    "Problem Solving",
                    "Perception",
                ),
                values = listOf(
                    widgetData?.cognitiveTraining?.subScores?.decisionMaking?.toInt() ?: 0,
                    widgetData?.cognitiveTraining?.subScores?.tacticalAwareness?.toInt() ?: 0,
                    widgetData?.cognitiveTraining?.subScores?.scanning?.toInt() ?: 0,
                    widgetData?.cognitiveTraining?.subScores?.perception?.toInt() ?: 0,
                    widgetData?.cognitiveTraining?.subScores?.problemSolving?.toInt() ?: 0
                ),
                chartColor = "#2563EB",
                score = widgetData?.cognitiveTraining?.score?.toInt() ?: 0,
                clubAvg = widgetData?.cognitiveTraining?.score?.toInt() ?: 0,
                isCoachPage = isCoachPage,
                sessionBorder = sessionBorder,
                forceFullWidth = forceFullWidth,
                trendData = recentTrend?.cognitiveTraining.orEmpty(),
                modifier = Modifier.width(cardWidth),
                tooltipTitle = WidgetTooltipContent.cognitiveTraining.title,
                tooltipText = WidgetTooltipContent.cognitiveTraining.text,
            )
        },
        9 to @Composable { displayNumber: Int ->
            CompetitiveEnvironmentWidget(
                modifier = Modifier.width(cardWidth),
                showSelection = showSelection,
                isSelected = selectedIds.contains(9),
                onSelectionChange = { names ->
                    onSelectionChange(9, names)
                },
                data = widgetData?.competitiveEnvironment ?: CompetitiveEnvironment(
                    score = 0,
                    subScores = CompetitiveEnvironmentSubScores(
                        competition = 0,
                        accountability = 0,
                        scoringSystems = 0,
                        timePressure = 0,
                        winningConditions = 0
                    )
                ),
                widgetNo = displayNumber,
                trendData = recentTrend?.competitiveEnvironment.orEmpty(),
                widgetName = "Competitive Environment Score",
                widgetColor = Palette.DefensiveOrganizationRed.rgbLight,
                isCoachPage = isCoachPage,
                sessionBorder = sessionBorder,
                forceFullWidth = forceFullWidth,
                tooltipTitle = WidgetTooltipContent.competitiveEnvironment.title,
                tooltipText = WidgetTooltipContent.competitiveEnvironment.text,
                tooltipInline = true
            )
        },
        10 to @Composable { displayNumber: Int ->
            // In your dashboard or page
            MatchAnalysisNewWidget(
                modifier = Modifier.width(cardWidth),
                showSelection = showSelection,
                isCoachPage = isCoachPage,
                sessionBorder = sessionBorder,
                forceFullWidth = forceFullWidth,
                isSelected = selectedIds.contains(10),
                onSelectionChange = {
                    onSelectionChange(10, it)
                },
                data = widgetData?.matchAnalysis ?: MatchAnalysis(
                    rootCauses = emptyList(),
                    score = 0,
                    strengths = emptyList(),
                    subScores = MatchAnalysisSubScores(
                        inPossession = 0,
                        outOfPossession = 0,
                        transitions = 0
                    ),
                    trainingPriorities = emptyList(),
                    weaknesses = emptyList()
                ),
                widgetNo = displayNumber,
                trendData = recentTrend?.matchAnalysis.orEmpty(),
                widgetName = "Match Analysis Report",
                widgetColor = Palette.CoachTalkPrimaryBlue.rgbLight,
                tooltipTitle = WidgetTooltipContent.matchAnalysis.title,
                tooltipText = WidgetTooltipContent.matchAnalysis.text

            )
        }
    )
}

fun widgetTitleFontSize(
    showSelection: Boolean,
    sessionBorder: Boolean,
    breakpoint: Breakpoint
): CSSSizeValue<CSSUnit.px> {
    return when {
        sessionBorder -> 14.px
        showSelection || breakpoint < Breakpoint.MD -> 15.px
        else -> 17.px
    }
}